<template>
  <div class="pa-6 b2c-v2-report">
    <h1 class="text-h5 font-weight-bold mb-1">MRR B2C / B2B2C</h1>
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
      <div class="body-2 grey--text text--darken-1">{{ tabSubtitle }}</div>
      <v-spacer />
      <v-select v-model="country" :items="countryOptions" label="País" outlined dense hide-details class="country-filter" />
    </div>

    <v-tabs v-model="tab" class="mb-4">
      <v-tab v-for="t in tabs" :key="t.key" :href="`#${t.key}`">{{ t.label }}</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" class="transparent">
      <!-- ═══ MÉTRICAS ═══ -->
      <v-tab-item value="metricas">
        <v-card outlined class="mb-4 pa-4">
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="3">
              <v-menu
                v-model="menuDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="displayDate(date)"
                    label="Fecha de corte"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="date" :min="minDate" :max="today" no-title @input="menuDate = false" />
              </v-menu>
            </v-col>
          </v-row>
        </v-card>

        <!-- Cantidades: 2 cajas principales y luego sus 4 partes -->
        <div class="pw-section mb-2">Membresías</div>
        <v-row dense>
          <v-col v-for="box in mainBoxes" :key="`count-${box.key}`" cols="12" md="6">
            <B2CV2KpiCard
              :label="box.label"
              :value="snapshot.kpis[box.key].count"
              :details="box.details"
              :loading="loadingSnapshot"
              :inverse="box.inverse"
              main
              clickable
              @select="openDetail(box)"
            />
          </v-col>
        </v-row>
        <v-row dense class="mb-4">
          <v-col v-for="box in partBoxes" :key="`count-${box.key}`" cols="12" sm="6" md="3">
            <B2CV2KpiCard
              :label="box.label"
              :value="snapshot.kpis[box.key].count"
              :details="box.details"
              :loading="loadingSnapshot"
              clickable
              @select="openDetail(box)"
            />
          </v-col>
        </v-row>

        <!-- MRR: misma disposición -->
        <div class="pw-section mb-2">MRR</div>
        <v-row dense>
          <v-col v-for="box in mainBoxes" :key="`mrr-${box.key}`" cols="12" md="6">
            <B2CV2KpiCard
              :label="box.label"
              :value="snapshot.kpis[box.key].mrrUsd"
              value-type="usd"
              :loading="loadingSnapshot"
              :inverse="box.inverse"
              main
            />
          </v-col>
        </v-row>
        <v-row dense class="mb-4">
          <v-col v-for="box in partBoxes" :key="`mrr-${box.key}`" cols="12" sm="6" md="3">
            <B2CV2KpiCard
              :label="box.label"
              :value="snapshot.kpis[box.key].mrrUsd"
              value-type="usd"
              :loading="loadingSnapshot"
            />
          </v-col>
        </v-row>

        <v-alert v-if="dataQualityNotes.length" type="info" outlined dense class="body-2">
          <div v-for="note in dataQualityNotes" :key="note">{{ note }}</div>
        </v-alert>
      </v-tab-item>

      <!-- ═══ MRR DIARIO ═══ (v-tab-item monta su contenido recién la primera vez que se abre) -->
      <v-tab-item value="diario">
        <B2CV2DailyPanel :country="country" :today="today" />
      </v-tab-item>

      <!-- ═══ MRR ANUAL ═══ -->
      <v-tab-item value="anual">
        <B2CV2StockChart
          :year="trend.year"
          :months="trend.months"
          :loading="loadingTrend"
          class="mb-4"
          @select-bar="openBar"
        />

        <B2CV2BarDetail
          v-if="bar.selection"
          :selection="bar.selection"
          :rows="bar.rows"
          :loading="bar.loading"
          :error="bar.error"
          class="mb-4"
          @close="bar.selection = null"
        />
      </v-tab-item>
    </v-tabs-items>

    <B2CV2DetailDialog
      v-model="detail.open"
      :title="detail.title"
      :subtitle="detailSubtitle"
      :contracts="detail.contracts"
      :contract="detail.contract"
      :rows="detail.rows"
      :loading="detail.loading"
      :error="detail.error"
      :show-comment="detail.box === 'loyalty'"
      :box="detail.box"
      @select-contract="openContract"
      @back="detail.contract = null"
      @save-note="saveNote"
    />

    <v-snackbar v-model="errorSnackbar" color="error" timeout="5000">{{ errorMessage }}</v-snackbar>
    <v-snackbar v-model="successSnackbar" color="success" timeout="3000">Comentario guardado</v-snackbar>
  </div>
</template>

<script>
import B2CV2KpiCard from '@/components/b2c-report-v2/B2CV2KpiCard.vue';
import B2CV2StockChart from '@/components/b2c-report-v2/B2CV2StockChart.vue';
import B2CV2DetailDialog from '@/components/b2c-report-v2/B2CV2DetailDialog.vue';
import B2CV2BarDetail from '@/components/b2c-report-v2/B2CV2BarDetail.vue';
import B2CV2DailyPanel from '@/components/b2c-report-v2/B2CV2DailyPanel.vue';
import B2CReportV2Service from '@/services/b2c-report-v2.service';
import { formatNumber } from '@/utils/b2c-report.utils';
import { COUNTRY_OPTIONS } from '@/utils/b2c-report-v2.labels';

// Desde esta fecha el historial de estados está completo; antes no hay foto confiable.
const MIN_CUTOFF = '2026-07-01';

/** Hoy en Chile, como YYYY-MM-DD: la fecha de corte es un día chileno, igual que en el backend. */
export const santiagoToday = (now = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(now);

// Pestañas de la vista; la elegida queda en `?tab=` para sobrevivir a la recarga.
const TABS = [
  { key: 'metricas', label: 'Métricas', subtitle: 'Membresías activas a la fecha de corte. Haz clic en una cantidad para ver el listado.' },
  { key: 'diario', label: 'MRR Diario', subtitle: 'MRR del total B2C/B2B2C día a día. Haz clic en un día para ver sus altas y bajas.' },
  { key: 'anual', label: 'MRR Anual', subtitle: 'Membresías y MRR al cierre de cada mes, por línea de negocio.' }
];
const DEFAULT_TAB = 'metricas';

const emptyKpi = () => ({ count: 0, mrrUsd: 0, contracts: [] });

// Cajas que se abren por contrato antes de llegar a las membresías (pedido de Guille). B2C se
// abre por canal: Directo y cada broker.
const BOXES_BY_CONTRACT = ['total', 'bci', 'b2b2c', 'upsell'];

// Fila "Directo" de la lista de B2C: se pide como grupo, no por contrato.
const DIRECT_GROUP = 'direct';

// Valor del filtro de contrato para las membresías sin convenio (igual que el backend).
const NO_CONTRACT = 'none';

const emptyDetail = () => ({
  open: false,
  box: null,
  title: '',
  contracts: [],
  contract: null,
  rows: [],
  loading: false,
  error: ''
});

const emptySnapshot = () => ({
  date: null,
  kpis: {
    total: emptyKpi(),
    bci: emptyKpi(),
    b2c: emptyKpi(),
    b2b2c: emptyKpi(),
    upsell: emptyKpi(),
    loyalty: emptyKpi()
  },
  segments: [],
  b2cGroups: [],
  excluded: { upsell: { count: 0, bci: 0 } },
  dataQuality: { unknownChannel: 0, missingHistory: 0 }
});

export default {
  name: 'B2CReportV2',
  components: { B2CV2KpiCard, B2CV2StockChart, B2CV2DetailDialog, B2CV2BarDetail, B2CV2DailyPanel },
  data() {
    const today = santiagoToday();
    const queryTab = this.$route?.query?.tab;
    const tab = TABS.some((t) => t.key === queryTab) ? queryTab : DEFAULT_TAB;
    return {
      tabs: TABS,
      tab,
      today,
      minDate: MIN_CUTOFF,
      date: today,
      menuDate: false,
      // Chile por defecto (pedido de Guille); "Todos" sigue disponible en el filtro.
      country: 'CL',
      countryOptions: COUNTRY_OPTIONS,
      loadingSnapshot: false,
      loadingTrend: false,
      snapshot: emptySnapshot(),
      trend: { year: Number(today.slice(0, 4)), months: [] },
      // Cada pestaña carga su data la primera vez que se abre (FR-004).
      loaded: { metricas: false, anual: false },
      detail: emptyDetail(),
      bar: { selection: null, rows: [], loading: false, error: '' },
      errorSnackbar: false,
      errorMessage: '',
      successSnackbar: false
    };
  },
  computed: {
    tabSubtitle() {
      return (TABS.find((t) => t.key === this.tab) || TABS[0]).subtitle;
    },
    /** Las dos cajas principales: el total B2C/B2B2C y sus pólizas BCI (contraste invertido). */
    mainBoxes() {
      return [
        { key: 'total', label: 'B2C/B2B2C', details: this.partsOf('count') },
        { key: 'bci', label: 'BCI Seguros', details: this.partsOf('bci'), inverse: true }
      ];
    },
    /** Las cuatro partes: B2C (Directo + Brokers), B2B2C y Upsell suman el total; Loyalty va aparte. */
    partBoxes() {
      return [
        { key: 'b2c', label: 'B2C', details: this.b2cLines() },
        {
          key: 'b2b2c',
          label: 'B2B2C',
          details: [
            { label: 'Pago Directo', value: this.segmentCount('b2b2cDirectPay') },
            { label: 'por Planilla', value: this.segmentCount('b2b2cPayroll') }
          ]
        },
        {
          key: 'upsell',
          label: 'Upsell de contratos B2B',
          details: this.snapshot.kpis.upsell.contracts.map((c) => ({
            label: c.companyName || c.contractCode || 'Sin contrato',
            value: formatNumber(c.count)
          }))
        },
        { key: 'loyalty', label: 'Pawer Loyalty', details: [] }
      ];
    },
    detailSubtitle() {
      const country = this.countryOptions.find((o) => o.value === this.country)?.text || 'Todos';
      return `Al ${this.displayDate(this.date)} · ${country}`;
    },
    dataQualityNotes() {
      const dq = this.snapshot.dataQuality || {};
      const notes = [];
      if (dq.unknownChannel) {
        notes.push(`${formatNumber(dq.unknownChannel)} membresías sin canal reconocible.`);
      }
      if (dq.missingHistory) {
        notes.push(`${formatNumber(dq.missingHistory)} membresías se desactivaron sin registro en el historial de estados.`);
      }
      return notes;
    }
  },
  watch: {
    date: 'loadSnapshot',
    // El panel diario recarga solo con su prop `country`; aquí, lo que ya se cargó.
    country() {
      if (this.loaded.metricas) this.loadSnapshot();
      if (this.loaded.anual) this.loadTrend();
      this.bar.selection = null;
    },
    tab(value) {
      this.loadTab(value);
      if (this.$route && this.$route.query.tab !== value) {
        this.$router.replace({ query: { ...this.$route.query, tab: value } });
      }
    }
  },
  mounted() {
    this.loadTab(this.tab);
  },
  methods: {
    formatNumber,
    /** Carga la data de una pestaña la primera vez que se abre; el diario se carga solo. */
    loadTab(key) {
      if (key === 'metricas' && !this.loaded.metricas) {
        this.loaded.metricas = true;
        this.loadSnapshot();
      }
      if (key === 'anual' && !this.loaded.anual) {
        this.loaded.anual = true;
        this.loadTrend();
      }
    },
    displayDate(value) {
      if (!value) return '';
      const [y, m, d] = value.split('-');
      return `${d}/${m}/${y}`;
    },
    segmentCount(key) {
      return formatNumber(this.snapshot.segments.find((s) => s.segment === key)?.count || 0);
    },
    segmentOf(key) {
      return this.snapshot.segments.find((s) => s.segment === key) || {};
    },
    /**
     * Las tres partes del total (B2C, B2B2C y Upsell). `field` = 'count' para todas,
     * 'bci' para sus pólizas BCI.
     */
    partsOf(field) {
      const b2c = (this.segmentOf('b2cDirect')[field] || 0) + (this.segmentOf('b2cBrokers')[field] || 0);
      const b2b2c = (this.segmentOf('b2b2cDirectPay')[field] || 0) + (this.segmentOf('b2b2cPayroll')[field] || 0);
      const upsell = ((this.snapshot.excluded || {}).upsell || {})[field] || 0;
      return [
        { label: 'B2C', value: formatNumber(b2c) },
        { label: 'B2B2C', value: formatNumber(b2b2c) },
        { label: 'Upsell', value: formatNumber(upsell) }
      ];
    },
    /** B2C = Directo + Brokers integrados + Felix Dedicado ("Sin modalidad" solo si hay). */
    b2cLines() {
      const modality = (key) => (this.segmentOf('b2cBrokers').modalities || []).find((m) => m.modality === key) || {};
      const lines = [
        { label: 'Directo', value: this.segmentOf('b2cDirect').count || 0 },
        { label: 'Integrados', value: modality('integrated').count || 0 },
        { label: 'Felix Dedicado', value: modality('dedicatedFelix').count || 0 },
        { label: 'Sin modalidad', value: modality('unclassified').count || 0, onlyIfAny: true }
      ];
      return lines
        .filter((l) => !l.onlyIfAny || l.value > 0)
        .map((l) => ({ label: l.label, value: formatNumber(l.value) }));
    },
    showError(message) {
      this.errorMessage = message;
      this.errorSnackbar = true;
    },
    /** Guarda el comentario de una membresía de Loyalty y lo refleja en la fila sin recargar. */
    async saveNote({ membershipId, note }) {
      const result = await B2CReportV2Service.updateLoyaltyNote(membershipId, note);
      if (result.status !== 'ok') {
        this.showError(result.message);
        return;
      }
      const row = this.detail.rows.find((r) => r.membershipId === membershipId);
      if (row) row.comment = result.data.note;
      this.successSnackbar = true;
    },
    async loadSnapshot() {
      this.loadingSnapshot = true;
      const result = await B2CReportV2Service.getSnapshot({ date: this.date, country: this.country });
      this.loadingSnapshot = false;
      if (result.status !== 'ok') {
        this.showError(result.message);
        return;
      }
      this.snapshot = result.data;
    },
    async loadTrend() {
      this.loadingTrend = true;
      const result = await B2CReportV2Service.getTrend({ year: this.trend.year, country: this.country });
      this.loadingTrend = false;
      if (result.status !== 'ok') {
        this.showError(result.message);
        return;
      }
      this.trend = result.data;
    },
    /** Lista las altas y bajas del mes elegido, de todas las líneas. */
    async openBar(selection) {
      this.bar = { selection, rows: [], loading: true, error: '' };
      const result = await B2CReportV2Service.getTrendDetail({ ...selection, country: this.country });
      // Si eligieron otra barra mientras cargaba, esta respuesta ya no aplica.
      if (this.bar.selection !== selection) return;
      this.bar.loading = false;
      if (result.status !== 'ok') {
        this.bar.error = result.message;
        return;
      }
      this.bar.rows = result.data;
    },
    /** BCI, B2B2C y Upsell abren primero sus contratos; B2C y Loyalty, sus membresías. */
    async openDetail(box) {
      this.detail = { ...emptyDetail(), open: true, box: box.key, title: box.label };
      if (box.key === 'b2c') {
        this.detail.contracts = this.snapshot.b2cGroups || [];
        return;
      }
      if (BOXES_BY_CONTRACT.includes(box.key)) {
        this.detail.contracts = this.snapshot.kpis[box.key].contracts;
        return;
      }
      await this.loadRows(box.key, {});
    },
    /** Abre un contrato de la lista, o un canal de la lista de B2C ("Directo" o un broker). */
    async openContract(contract) {
      this.detail.contract = contract;
      const filter = contract.key === DIRECT_GROUP
        ? { group: DIRECT_GROUP }
        : { contract: contract.contractCode || NO_CONTRACT };
      await this.loadRows(this.detail.box, filter);
    },
    async loadRows(box, filter) {
      this.detail.rows = [];
      this.detail.loading = true;
      this.detail.error = '';
      const opened = this.detail.contract;
      const result = await B2CReportV2Service.getDetail({ box, ...filter, date: this.date, country: this.country });
      // Si cerraron, cambiaron de caja o de contrato mientras cargaba, esta respuesta ya no aplica.
      if (this.detail.box !== box || this.detail.contract !== opened) return;
      this.detail.loading = false;
      if (result.status !== 'ok') {
        this.detail.error = result.message;
        return;
      }
      this.detail.rows = result.data;
    }
  }
};
</script>

<style scoped>
.b2c-v2-report {
  max-width: 1400px;
}
.country-filter {
  max-width: 220px;
}
</style>
